import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, LogOut, BadgeCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { ui } from "../lib/ui";

function Wordmark() {
  return (
    <span className="font-serif text-sm tracking-[0.2em] uppercase font-bold text-[#8B1A1A]">
      Élite TEF Prep
    </span>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const links = [
    { to: "/archive", label: "Archive", testid: "nav-archive-link" },
    { to: "/about", label: "About", testid: "nav-about-link" },
  ];

  const linkClass = ({ isActive }) =>
    `font-sans text-sm font-medium px-3 py-2 rounded-lg transition-colors ${
      isActive ? "text-[#8B1A1A] bg-[#F3E5D8]" : "text-[#210201] hover:text-[#8B1A1A]"
    }`;

  return (
    <header className="sticky top-0 z-50 bg-[#F5EAE0]/90 backdrop-blur-md border-b border-[#E8DCC8]">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex flex-col leading-none" data-testid="nav-brand-link">
          <span className="font-serif text-xl font-bold text-[#8B1A1A]">Report Your TEF</span>
          <Wordmark />
        </Link>

        <nav className="hidden md:flex items-center gap-2">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkClass} data-testid={l.testid}>
              {l.label}
            </NavLink>
          ))}
          {user && user.role === "admin" && (
            <NavLink to="/admin" className={linkClass} data-testid="nav-admin-link">
              Admin
            </NavLink>
          )}
          {user && (user.access_level === "member" || user.role === "admin") && (
            <span className="inline-flex items-center gap-1 bg-[#F3E5D8] text-[#8B1A1A] text-xs font-semibold px-2.5 py-1 rounded-full border border-[#E8DCC8]" data-testid="member-badge">
              <BadgeCheck size={13} /> Member
            </span>
          )}
          <Link to="/submit" className={ui.btnPrimary + " ml-2 !px-5 !py-2.5"} data-testid="nav-submit-report-btn">
            Report my exam
          </Link>
          {user ? (
            <button
              onClick={async () => { await logout(); navigate("/"); }}
              className={ui.btnGhost}
              data-testid="nav-logout-btn"
              title="Log out"
            >
              <LogOut size={16} />
            </button>
          ) : (
            <NavLink to="/login" className={linkClass} data-testid="nav-login-link">
              Login
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2 md:hidden">
          <Link
            to="/submit"
            className={ui.btnPrimary + " !px-4 !py-2 text-sm"}
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-report-btn"
          >
            Report
          </Link>
          <button
            className="text-[#8B1A1A] p-2 -mr-2"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#E8DCC8] bg-[#F5EAE0] px-4 py-4 flex flex-col gap-1">
          <Link to="/submit" className={ui.btnPrimary + " w-full"} onClick={() => setOpen(false)} data-testid="nav-mobile-submit-btn">
            Report my exam
          </Link>
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkClass} onClick={() => setOpen(false)}>
              {l.label}
            </NavLink>
          ))}
          {user && user.role === "admin" && (
            <NavLink to="/admin" className={linkClass} onClick={() => setOpen(false)}>Admin</NavLink>
          )}
          {user && (user.access_level === "member" || user.role === "admin") && (
            <span className="inline-flex items-center gap-1 w-fit bg-[#F3E5D8] text-[#8B1A1A] text-xs font-semibold px-2.5 py-1 rounded-full border border-[#E8DCC8]">
              <BadgeCheck size={13} /> Member
            </span>
          )}
          {user ? (
            <button onClick={async () => { await logout(); setOpen(false); navigate("/"); }} className={linkClass + " text-left"}>
              Log out
            </button>
          ) : (
            <NavLink to="/login" className={linkClass} onClick={() => setOpen(false)}>Login</NavLink>
          )}
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="bg-[#671813] text-[#F5EAE0] mt-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12 flex flex-col sm:flex-row justify-between gap-6">
        <div>
          <div className="font-serif text-lg font-bold mb-1">Report Your TEF</div>
          <p className="text-sm text-[#F3E5D8] max-w-sm leading-relaxed">
            A community resource for TEF Canada candidates. Reports are moderated and are not official
            TEF / CCI Paris Île-de-France content.
          </p>
        </div>
        <div className="text-sm">
          <a
            href="https://elitetefprep.com"
            target="_blank"
            rel="noopener noreferrer"
            className="font-serif tracking-[0.2em] uppercase font-bold hover:text-white transition-colors"
            data-testid="footer-elite-link"
          >
            An Élite TEF Prep project
          </a>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="App flex flex-col min-h-screen">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
